import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Mail, Lock, User, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, signup } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const routeFor = (u) => u.role === "admin" ? "/admin" : u.role === "employee" ? "/staff" : "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        const u = await login(f.email, f.password);
        toast.success(`Welcome back, ${u.name}!`);
        nav(routeFor(u));
      } else {
        if (!f.name || !f.email || !f.password) { toast.error("Please fill all fields"); return; }
        await signup(f);
        toast.success("Account created!");
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
  };

  return (
    <div style={{ minHeight: "calc(100vh - var(--site-header-height, 148px))" }} className="grid lg:grid-cols-2">
      <div className="hidden lg:flex relative grid-bg items-center justify-center overflow-hidden border-r border-black/10">
        <div className="absolute -top-24 -left-10 w-96 h-96 rounded-full bg-orange-600/20 blur-[120px] animate-float-slow" />
        <div className="relative text-center px-12">
          <div className="w-16 h-16 rounded-2xl orange-gradient grid place-items-center mx-auto orange-glow"><Package className="w-9 h-9 text-white" /></div>
          <h2 className="font-display font-bold text-3xl mt-6">Smart<span className="orange-gradient-text">Logistics</span></h2>
          <p className="text-neutral-600 mt-4 max-w-sm mx-auto">Track your parcels, manage requests and stay connected with our team — all in one place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md animate-fade-up">
          <h1 className="font-display font-bold text-3xl">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-neutral-600 mt-2">{isLogin ? "Log in to manage your parcels" : "Sign up to start sending parcels"}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-neutral-700 text-sm">Full name</Label>
                <div className="relative mt-1.5"><User className="w-4 h-4 absolute left-3 top-3 text-neutral-500" /><Input value={f.name} onChange={set("name")} placeholder="Your name" className="bg-black/[0.03] border-black/10 pl-9" /></div>
              </div>
            )}
            <div>
              <Label className="text-neutral-700 text-sm">Email</Label>
              <div className="relative mt-1.5"><Mail className="w-4 h-4 absolute left-3 top-3 text-neutral-500" /><Input value={f.email} onChange={set("email")} type="email" placeholder="you@email.com" className="bg-black/[0.03] border-black/10 pl-9" /></div>
            </div>
            {!isLogin && (
              <div>
                <Label className="text-neutral-700 text-sm">Phone</Label>
                <div className="relative mt-1.5"><Phone className="w-4 h-4 absolute left-3 top-3 text-neutral-500" /><Input value={f.phone} onChange={set("phone")} placeholder="+39 …" className="bg-black/[0.03] border-black/10 pl-9" /></div>
              </div>
            )}
            <div>
              <Label className="text-neutral-700 text-sm">Password</Label>
              <div className="relative mt-1.5"><Lock className="w-4 h-4 absolute left-3 top-3 text-neutral-500" /><Input value={f.password} onChange={set("password")} type="password" placeholder="••••••••" className="bg-black/[0.03] border-black/10 pl-9" /></div>
            </div>
            <Button type="submit" className="w-full orange-gradient text-white border-0 h-11 font-semibold hover:opacity-90">{isLogin ? "Log in" : "Create account"}</Button>
          </form>

          <p className="text-sm text-neutral-600 mt-6 text-center">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <Link to={isLogin ? "/signup" : "/login"} className="text-orange-500 hover:text-orange-400 font-medium">{isLogin ? "Sign up" : "Log in"}</Link>
          </p>

        </div>
      </div>
    </div>
  );
}
